import { AlertCircle, Loader2 } from 'lucide-react';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError, NetworkError } from '@/lib/api-client';
import { useAuthStore } from '@/stores/authStore';
import { STRINGS } from '@/strings/strings';

type FormError = 'throttled' | 'generic' | 'network';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ForgotPasswordRoute() {
  const requestPasswordReset = useAuthStore((s) => s.requestPasswordReset);

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [formError, setFormError] = useState<FormError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const validateEmail = (value: string): string | null => {
    if (!value.trim()) return STRINGS.auth.errors.emailRequired;
    if (!EMAIL_RE.test(value)) return STRINGS.auth.errors.emailInvalid;
    return null;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const err = validateEmail(email);
    setEmailError(err);
    if (err) return;

    setFormError(null);
    setIsSubmitting(true);
    try {
      await requestPasswordReset({ email });
      setSubmitted(true);
    } catch (err) {
      if (err instanceof NetworkError) {
        setFormError('network');
      } else if (err instanceof ApiError && err.httpStatus === 429) {
        // Backend forgot-password throttle: 3 attempts / 1 hour per IP
        // (auth.controller.ts). TTL in the user-facing copy matches
        // the backend `@Throttle` window.
        setFormError('throttled');
      } else {
        setFormError('generic');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const errorMessage =
    formError === 'throttled'
      ? STRINGS.auth.errors.forgotPasswordThrottled
      : formError === 'network'
        ? STRINGS.auth.errors.networkError
        : formError === 'generic'
          ? STRINGS.auth.errors.genericBackend
          : null;

  if (submitted) {
    return (
      <>
        <h1 className="text-[22px] font-semibold text-dream-white mb-4">
          {STRINGS.auth.forgot.title}
        </h1>
        <div
          role="status"
          className="mb-6 p-4 rounded-md bg-white/5 border border-white/10 text-sm text-dream-white/90 space-y-2"
        >
          <p>{STRINGS.auth.forgot.successBody}</p>
          <p className="text-dream-white/70">{STRINGS.auth.forgot.spamHint}</p>
        </div>
        <Link
          to="/auth/login"
          className="block text-center text-sm text-dream-white/80 underline hover:text-dream-white"
        >
          {STRINGS.auth.forgot.backToLogin}
        </Link>
      </>
    );
  }

  return (
    <>
      <h1 className="text-[22px] font-semibold text-dream-white mb-2">
        {STRINGS.auth.forgot.title}
      </h1>
      <p className="text-sm text-dream-white/80 mb-6 leading-relaxed">
        {STRINGS.auth.forgot.description}
      </p>

      {errorMessage && (
        <div
          role="alert"
          className="flex items-start gap-2 mb-4 p-3 rounded-md bg-[rgba(226,75,74,0.15)] border border-[rgba(226,75,74,0.4)] text-[#FCA5A5] text-sm"
        >
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{errorMessage}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="forgot-email" className="text-sm text-dream-white">
            {STRINGS.auth.forgot.emailLabel}
          </Label>
          <Input
            id="forgot-email"
            type="email"
            autoComplete="email"
            inputMode="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError(null);
            }}
            onBlur={() => setEmailError(validateEmail(email))}
            aria-invalid={Boolean(emailError)}
            aria-describedby={emailError ? 'forgot-email-error' : undefined}
            className="h-11 bg-dream-white text-dream-navy"
          />
          {emailError && (
            <p id="forgot-email-error" className="text-xs text-[color:var(--color-error)]">
              {emailError}
            </p>
          )}
        </div>

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full h-12 text-base font-semibold"
        >
          {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {isSubmitting ? STRINGS.auth.forgot.loadingState : STRINGS.auth.forgot.submitButton}
        </Button>
      </form>

      <div className="mt-6 text-center">
        <Link
          to="/auth/login"
          className="text-sm text-dream-white/80 underline hover:text-dream-white"
        >
          {STRINGS.auth.forgot.backToLogin}
        </Link>
      </div>
    </>
  );
}
